/**
 * Resolve the browser-reachable host for a LAUNCHED brand's deployed site, for the website PREVIEW.
 *
 * The preview embeds the brand's real deployed site in a sandboxed iframe (`LivePreview`). The brand
 * record only carries the brand's PUBLIC domain (e.g. `velaro.casino`), which off production is
 * usually never purchased / connected — so loading it in the iframe just fails (DNS/connection error)
 * and the preview never appears.
 *
 * casino-creation knows the always-reachable INTERNAL host it deployed the site under
 * (e.g. `velaro-6eec0800.spark-test.newtoncelestial.com`). `GET /casino/tenants/by-domain?domain=…`
 * returns it keyed by the public domain we already have. We prefer that internal host for the preview
 * iframe — exactly as onboarding's deploy step prefers the deploy response's `internal_domain` over
 * the configurator preview for its "Preview Website" button (`deploy.domain || previewUrl`).
 *
 * WHY ONLY THE SANDBOXED IFRAME SWITCHES. Every backend call keeps passing the PUBLIC domain
 * (`?domain=…`): it is the services' canonical site KEY, resolved server-side to the tenant's guest
 * token / real site_id with no browser fetch, so it works off-prod regardless of DNS. The navigable
 * "view on site" link also keeps the public domain: it is the brand's real, shareable address, and a
 * standing link must not expose the internal cluster host (a direct-to-origin URL past the public
 * domain's edge protections on prod). Only the sandboxed iframe's src takes the internal host.
 *
 * WHY THE PROXY. casino-creation is a per-cluster service, unreachable from the browser. It is called
 * through the owner-hub MLP proxy (`_svc=cc`, cluster scope `c` → forwarded to the brand's
 * destination cluster) with the owner's own token — the same transport the logo-family reads use
 * (`../casino-settings/logo-api`), which is what makes it work on a launched brand whose configurator
 * session is terminal.
 */
import React from 'react';
import axios from 'axios';

// Reuse the page's existing casino-creation (cluster scope) transport rather than constructing a
// third identical copy — see ./creative-api and ../casino-settings/logo-api. It carries the raw owner
// token and opts out of the auth interceptor's teardown: a downstream 401 is that service's answer,
// not an owner-hub session verdict.
import { casinoCreation } from './creative-api';

/** One row of casino-creation's `by-domain` lookup. Only the fields we read are typed; the endpoint
 *  also returns `website_url`, `pipeline_id`, `pipeline_status`. */
export interface CasinoByDomainRow {
  tenant_id?: number | string;
  domain?: string;
  internal_domain?: string;
}

interface CasinoByDomainResponse {
  casinos?: CasinoByDomainRow[];
}

/**
 * DEV-ONLY preview-host override (`?previewHost=`), for when the brand's own hosts cannot serve the
 * preview: its public domain is not connected off production, and its internal host can be down or
 * unknown to the lookup below. It also points the preview at a site running on your own machine,
 * which is the only way to exercise a bridge change before it is deployed anywhere.
 *
 *   ?previewHost=localhost:7000        the local site (http is assumed for localhost)
 *   ?previewHost=brand.example.com     any reachable host
 *   ?previewHost=off                   back to the normal resolution
 *
 * It is remembered (localStorage) so it survives the navigation inside the Studio, and it is read
 * ONLY when the Studio itself is served from localhost — a deployed bundle ignores it entirely, so
 * it can never point a real owner's preview anywhere but their own site. Like the internal host, it
 * changes the iframe's src and nothing else: every backend call keeps passing the brand's real
 * public domain.
 */
export const PREVIEW_HOST_OVERRIDE_KEY = 'ows-preview-host';

/** A local host carries no scheme and is not served over TLS, so `resolveSiteUrl`'s https default
 *  would break it. Anything else is left exactly as typed. */
const withLocalScheme = (host: string): string =>
  /^https?:\/\//i.test(host) || !/^(localhost|127\.0\.0\.1)(:|$)/i.test(host) ? host : `http://${host}`;

/**
 * The override to use, and what should be stored for next time: `undefined` leaves the stored value
 * alone, `null` clears it. Pure, so the URL/storage plumbing stays in one thin wrapper below.
 */
export const resolvePreviewHostOverride = (
  search: string,
  stored: string | null,
): { host: string | null; store: string | null | undefined } => {
  const asked = new URLSearchParams(search).get('previewHost');
  if (asked === null) return { host: cleanHost(stored ?? undefined), store: undefined };
  const value = asked.trim();
  if (value === '' || value === 'off') return { host: null, store: null };
  return { host: withLocalScheme(value), store: withLocalScheme(value) };
};

/** True when this Studio is the one running on your own machine. */
export const onLocalStudio = (): boolean => /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);

/** `resolvePreviewHostOverride` against the real URL and storage. Local Studio only; never throws. */
const previewHostOverride = (): string | null => {
  try {
    if (!onLocalStudio()) return null;
    const { host, store } = resolvePreviewHostOverride(window.location.search, localStorage.getItem(PREVIEW_HOST_OVERRIDE_KEY));
    if (store === null) localStorage.removeItem(PREVIEW_HOST_OVERRIDE_KEY);
    else if (store !== undefined) localStorage.setItem(PREVIEW_HOST_OVERRIDE_KEY, store);
    return host;
  } catch {
    // A blocked localStorage must not take the preview down with it.
    return null;
  }
};

/** Trim a candidate host to a non-empty string, or null. */
const cleanHost = (value: string | undefined): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : null;

/**
 * Pick the internal (cluster) host out of a `by-domain` payload.
 *
 * The query is by the brand's EXACT public domain, so the normal answer is a single row and we use
 * it (the domain is the authoritative key). Only when the lookup returns several rows do we
 * disambiguate by tenant. Returns null when no usable internal host is present. Pure — unit-tested.
 */
export const pickInternalDomain = (
  rows: CasinoByDomainRow[] | undefined,
  tenantId: number | string | undefined,
): string | null => {
  const list = rows ?? [];
  if (list.length === 0) return null;
  if (list.length === 1) {
    // The lone row IS the brand (queried by its exact public domain) — but if it carries a tenant
    // that positively disagrees with ours, distrust it: a loosened / regressed server-side domain
    // filter must never embed another tenant's host. A row with no tenant_id is trusted (domain key).
    const [only] = list;
    if (only.tenant_id != null && tenantId != null && String(only.tenant_id) !== String(tenantId)) return null;
    return cleanHost(only.internal_domain);
  }
  const match = tenantId != null ? list.find((r) => String(r.tenant_id) === String(tenantId)) : undefined;
  return cleanHost(match?.internal_domain);
};

/**
 * GET casino-creation's `by-domain` lookup and return the tenant's internal host, or null when it
 * names none. The downstream `domain` query is forwarded through the proxy alongside `_svc`/`tenant_id`.
 */
export const fetchInternalDomain = async (
  tenantId: number,
  publicDomain: string,
  signal?: AbortSignal,
): Promise<string | null> => {
  const response = await axios.get<CasinoByDomainResponse>(
    casinoCreation.url('/casino/tenants/by-domain', tenantId),
    casinoCreation.requestConfig(signal, { domain: publicDomain }),
  );
  return pickInternalDomain(response.data?.casinos, tenantId);
};

// The internal host of every brand looked up this session, and the lookups in flight, keyed by tenant and public
// domain: the Game Lists warm-up, the Creatives pages and the Website hub's Game Lists card share one lookup per brand.
// A reload looks it up again.
const internalHosts = new Map<string, string>();
const lookingUp = new Map<string, Promise<string | null>>();

const brandHostKey = (tenantId: number, publicDomain: string): string => `${tenantId}|${publicDomain}`;

/** The brand's internal host if a lookup named one this session, else undefined: not looked up yet, still being looked
 *  up, its lookup named none, or it threw. */
export const cachedInternalDomain = (tenantId: number, publicDomain: string): string | undefined =>
  internalHosts.get(brandHostKey(tenantId, publicDomain));

/**
 * `fetchInternalDomain`, once per brand a session for every reader: the host already looked up, else the lookup in
 * flight, else one new lookup. It carries no reader's signal, so one reader leaving never cancels it for another; a
 * reader checks its own cancellation once it has the answer. Only a host is kept: an answer that names none is not (a
 * brand just created gets its internal host once its deploy registers it, and kept, the warm-up would have pinned it to
 * its public domain until a reload), nor a lookup that throws, so the next reader looks again. Rejects as
 * `fetchInternalDomain` does.
 */
export const fetchInternalDomainShared = (tenantId: number, publicDomain: string): Promise<string | null> => {
  const key = brandHostKey(tenantId, publicDomain);
  const known = internalHosts.get(key);
  if (known !== undefined) return Promise.resolve(known);
  const inFlight = lookingUp.get(key);
  if (inFlight) return inFlight;
  const lookup = fetchInternalDomain(tenantId, publicDomain)
    .then((host) => {
      if (host !== null) internalHosts.set(key, host);
      return host;
    })
    .finally(() => {
      lookingUp.delete(key);
    });
  lookingUp.set(key, lookup);
  return lookup;
};

/**
 * Choose which host the browser should load for the preview:
 *  - the internal host once known (preferred),
 *  - else nothing while the lookup is still running — so the preview holds its loader rather than
 *    attempting the possibly-unconnected public domain and flashing a connection error,
 *  - else the public domain as the last resort (lookup finished with no internal host, or it could
 *    not run — no tenant / no proxy).
 * Pure — unit-tested.
 */
export const pickPreviewDomain = (opts: {
  internalDomain: string | null | undefined;
  resolving: boolean;
  publicDomain: string | undefined;
}): string | undefined => {
  if (typeof opts.internalDomain === 'string' && opts.internalDomain !== '') return opts.internalDomain;
  if (opts.resolving) return undefined;
  return opts.publicDomain;
};

/**
 * The preview host `useBrandSiteHost` settles on for a brand, read outside React (the Game Lists warm-up): the dev
 * override when one is set, else the internal host the shared lookup names (`pickPreviewDomain` once it is over), else
 * the public domain (no tenant, no public domain, no proxy, or a lookup that threw). Never rejects.
 */
export const resolvePreviewDomain = async (
  tenantId: number | undefined,
  publicDomain: string | undefined,
): Promise<string | undefined> => {
  const override = previewHostOverride();
  if (override) return override;
  if (tenantId == null || !publicDomain || !casinoCreation.configured()) return publicDomain;
  try {
    const internalDomain = await fetchInternalDomainShared(tenantId, publicDomain);
    return pickPreviewDomain({ internalDomain, resolving: false, publicDomain });
  } catch {
    return publicDomain;
  }
};

export interface BrandSiteHost {
  /** The host to load in the browser for the brand's deployed site: internal (preferred) → nothing
   *  (while resolving) → public (fallback). Feed to `resolveSiteUrl` for the preview iframe src. */
  previewDomain: string | undefined;
  /** True while the internal-host lookup is in flight. */
  resolving: boolean;
}

/**
 * Resolve the browser-reachable host for the active brand's deployed site. See the module header for
 * why this exists and why only browser-loaded URLs use it. Re-resolves when the brand changes.
 */
export const useBrandSiteHost = (
  tenantId: number | undefined,
  publicDomain: string | undefined,
): BrandSiteHost => {
  // Read once per mount: the override is a dev switch, not something that changes under the page.
  const [override] = React.useState(previewHostOverride);
  // Whether the effect below will look the host up for this brand. `resolving` is seeded to it, on the first
  // render as on a brand switch, so no render reports the possibly-unconnected public domain as settled while
  // its lookup is about to start (a caller would load it, then drop it one render later).
  const lookupWillRun = !override && tenantId != null && !!publicDomain && casinoCreation.configured();
  // The host an earlier reader already looked up for this brand this session (the Game Lists warm-up, another page):
  // the state starts on it, so there is no lookup to wait for and `resolving` starts false.
  const cached = lookupWillRun ? cachedInternalDomain(tenantId, publicDomain) : undefined;
  // `undefined` = not looked up yet; `null` = looked up, no internal host; string = the internal host.
  const [internalDomain, setInternalDomain] = React.useState<string | null | undefined>(cached);
  const [resolving, setResolving] = React.useState(lookupWillRun && cached === undefined);

  // Reset SYNCHRONOUSLY in render when the brand changes, so no render ever pairs the NEW brand's
  // tenant/domain with the PREVIOUS brand's already-resolved internal host — the effect below only
  // runs after commit, one render too late to prevent that stale-host flash. (React's sanctioned
  // "adjust state while rendering" pattern; the `!==` guard makes it self-terminating.) `resolving`
  // is re-seeded the same way, so the transition shows the loader rather than the public domain.
  const brandKey = `${tenantId ?? ''}|${publicDomain ?? ''}`;
  const [resolvedKey, setResolvedKey] = React.useState(brandKey);
  if (resolvedKey !== brandKey) {
    setResolvedKey(brandKey);
    setInternalDomain(cached);
    setResolving(lookupWillRun && cached === undefined);
  }

  React.useEffect(() => {
    // Nothing to resolve without a tenant + public domain, when the proxy base is unconfigured, or
    // when a dev override already says which host to frame.
    if (override || tenantId == null || !publicDomain || !casinoCreation.configured()) {
      setInternalDomain(null);
      setResolving(false);
      return;
    }
    const known = cachedInternalDomain(tenantId, publicDomain);
    if (known !== undefined) {
      setInternalDomain(known);
      setResolving(false);
      return;
    }
    // The lookup is shared, so leaving does not cancel it (another reader may wait on it): the flag only drops a
    // superseded brand's late answer, which must never clobber the current one's state.
    let cancelled = false;
    setInternalDomain(undefined);
    setResolving(true);
    fetchInternalDomainShared(tenantId, publicDomain)
      .then((host) => {
        if (cancelled) return;
        setInternalDomain(host);
        setResolving(false);
      })
      .catch(() => {
        // A failed or absent lookup is not fatal: fall back to the public domain.
        if (cancelled) return;
        setInternalDomain(null);
        setResolving(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, publicDomain, override]);

  if (override) return { previewDomain: override, resolving: false };
  return { previewDomain: pickPreviewDomain({ internalDomain, resolving, publicDomain }), resolving };
};
